import { ArrowLeft, MessageCircle, Search } from "lucide-react";
import { BottomNav } from "@/app/components/bottom-nav";
import { Input } from "@/app/components/input";
import { useState, useEffect, useCallback } from "react";
import * as authClient from "@/app/api/auth-client";
import { apiConversationItemToConversation } from "@/app/api/auth-client";

export interface Conversation {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: Date;
  avatar?: string;
  unreadCount: number;
  type: "support" | "seller";
  adId?: string;
  adTitle?: string;
  /** Other party's location (City, ST) from user API. */
  location?: string;
}

interface ChatListPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  conversations: Conversation[];
  setConversations: (conversations: Conversation[] | ((prev: Conversation[]) => Conversation[])) => void;
  onSelectConversation: (conversationId: string) => void;
  /** Current user id (for showing the other party's display name when BFF enriches the list). */
  userId?: string;
  /** When true, do not render the bottom nav (e.g. when embedded in chat modal). */
  hideBottomNav?: boolean;
  /** When true, do not render the page header (e.g. when embedded in chat modal with its own header). */
  hidePageHeader?: boolean;
}

export function ChatListPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  conversations,
  setConversations,
  onSelectConversation,
  userId,
  hideBottomNav,
  hidePageHeader,
}: ChatListPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadConversations = useCallback(() => {
    setError(null);
    setLoading(true);
    authClient
      .getConversations({ limit: 50 })
      .then((res) => {
        const list = (res.items ?? []).map((item) => apiConversationItemToConversation(item, userId)) as Conversation[];
        setConversations(list);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [setConversations]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const filteredConversations = conversations.filter(conv =>
    conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = diff / (1000 * 60 * 60);
    
    if (hours < 24) {
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (hours < 48) {
      return "Ontem";
    } else {
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      });
    }
  };

  return (
    <>
      {!hidePageHeader && (
        <>
          {/* Header */}
          <header className="sticky top-0 z-40 bg-card border-b border-border shadow-sm">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={onBack}
                  className="p-2 hover:bg-accent rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h2 className="text-lg">Mensagens</h2>
              </div>
            </div>

            {/* Search */}
            <div className="px-4 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar conversas..."
              className="pl-10"
            />
          </div>
        </div>
      </header>
        </>
      )}

      {/* Search (when header hidden, e.g. in modal) */}
      {hidePageHeader && (
        <div className="px-4 pt-2 pb-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar conversas..."
              className="pl-10"
            />
          </div>
        </div>
      )}

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto pb-20">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <p className="text-sm text-muted-foreground">Carregando conversas...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <p className="text-sm text-muted-foreground text-center mb-4">
              Não foi possível carregar as conversas.
            </p>
            <button
              type="button"
              onClick={loadConversations}
              className="text-sm font-medium text-primary hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mb-4">
              <MessageCircle className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-center mb-2">Nenhuma conversa</h3>
            <p className="text-sm text-muted-foreground text-center">
              {searchQuery
                ? "Nenhuma conversa encontrada"
                : "Suas conversas aparecerão aqui"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredConversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => onSelectConversation(conversation.id)}
                className="w-full p-4 hover:bg-accent transition-colors text-left"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground shrink-0 overflow-hidden">
                    {conversation.type === "support" ? (
                      <MessageCircle className="w-6 h-6" />
                    ) : conversation.avatar ? (
                      <img
                        src={conversation.avatar}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-lg font-semibold">
                        {conversation.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium truncate">
                          {conversation.adTitle
                            ? `${conversation.name} - ${conversation.adTitle}`
                            : conversation.name}
                        </h3>
                        {conversation.location && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {conversation.location}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatTime(conversation.timestamp)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-muted-foreground line-clamp-1 flex-1">
                        {conversation.lastMessage}
                      </p>
                      {conversation.unreadCount > 0 && (
                        <span className="shrink-0 min-w-[20px] h-5 px-2 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                          {conversation.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {!hideBottomNav && (
        <BottomNav
          activeTab={activeTab}
          onTabChange={(tab) => onTabChange(tab as "home" | "favorites")}
          favoritesCount={favoritesCount}
          onMenuClick={onMenuClick}
          onPostAdClick={onPostAdClick}
          postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
        />
      )}
    </>
  );
}
